import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useInAppNotificationStore } from '../../stores/useInAppNotificationStore';
import { apiClient } from '../../services/api';
import { hasAuthData } from '../../utils/authHeaders';
import { matchesDisplayContext } from '../../utils/localized';
import NotificationPopup from './NotificationPopup';
import type { InAppNotification } from '../../types';

const LOADING_ANIMATION_DELAY = 3000; // Задержка после загрузки анимации (мс)

const NotificationManager = () => {
  const { notifications, loadNotifications, markAsRead } = useInAppNotificationStore();
  const [currentNotification, setCurrentNotification] = useState<InAppNotification | null>(null);
  const [notificationQueue, setNotificationQueue] = useState<InAppNotification[]>([]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  // Трекаем ID уже показанных/закрытых уведомлений, чтобы не показывать повторно
  const dismissedIds = useRef<Set<string>>(new Set());
  // ID уведомлений, которые уже попали в очередь — чтобы WS push не дублировал их
  const enqueuedIds = useRef<Set<string>>(new Set());

  // Load notifications on mount with delay for loading animation
  useEffect(() => {
    const loadInitial = async () => {
      await loadNotifications();
      setHasLoadedInitial(true);
    };

    loadInitial();

    // Ждём завершения анимации загрузки приложения
    const appReadyTimer = setTimeout(() => {
      setIsAppReady(true);
    }, LOADING_ANIMATION_DELAY);

    return () => clearTimeout(appReadyTimer);
  }, [loadNotifications]);

  // Track current path so the displayContext filter re-runs on navigation
  // (a notification can become eligible after the user moves to a matching
  // page).
  const location = useLocation();
  const currentPath = location.pathname;

  // Enqueue unread notifications once app is ready. Runs both on initial load
  // and whenever the store receives a new item (e.g. via WebSocket push) — and
  // whenever the route changes (some notifications gate on displayContext).
  useEffect(() => {
    if (!hasLoadedInitial || !isAppReady) return;

    const fresh = notifications.filter(
      n => !n.isRead
        && !dismissedIds.current.has(n.id)
        && !enqueuedIds.current.has(n.id)
        && matchesDisplayContext(n.displayContext, currentPath)
    );
    if (fresh.length === 0) return;

    fresh.forEach(n => enqueuedIds.current.add(n.id));
    setNotificationQueue(prev => [...prev, ...fresh]);
  }, [notifications, hasLoadedInitial, isAppReady, currentPath]);

  // Show next notification from queue. Skip items that no longer match the
  // current displayContext (user navigated away after enqueue) — re-queue
  // them so they pop again on a matching page.
  useEffect(() => {
    if (currentNotification || notificationQueue.length === 0) return;
    const [next, ...rest] = notificationQueue;
    if (!matchesDisplayContext(next.displayContext, currentPath)) {
      // Drop the enqueued mark so the next nav re-evaluates this one.
      enqueuedIds.current.delete(next.id);
      setNotificationQueue(rest);
      return;
    }
    setCurrentNotification(next);
    setNotificationQueue(rest);
  }, [currentNotification, notificationQueue, currentPath]);

  const handleDismiss = useCallback(async () => {
    if (!currentNotification) return;

    const id = currentNotification.id;
    // Сразу помечаем как закрытое, чтобы не показывать повторно
    dismissedIds.current.add(id);
    // Сначала обновляем локальный стейт, чтобы убрать попап мгновенно
    markAsRead(id);
    setCurrentNotification(null);

    // Потом отправляем на сервер (не блокируем UI)
    if (hasAuthData()) {
      try {
        await apiClient.user.markNotificationRead(id);
      } catch (error) {
        // Не критично — локально уже помечено
      }
    }
  }, [currentNotification, markAsRead]);

  if (!currentNotification) {
    return null;
  }

  return (
    <NotificationPopup
      notification={currentNotification}
      onDismiss={handleDismiss}
    />
  );
};

export default NotificationManager;

